// "Raise..." -- the office's one control for the four kinds of work that are born with no date
// (Session AMF, closing Session ALW's first flagged gap; Session ANG added the fourth).
//
// "AND NO ENGINEER" WAS TRUE OF ALL THREE UNTIL SESSION ANG AND IS NOW TRUE OF THREE OF THE FOUR.
// A tech visit may be given an engineer at creation, because the rate card prices one PER ENGINEER
// and the visit normally goes to whoever diagnosed it on the call-out. It is still optional, and it
// is still not a date: an assigned, undated visit is on the Needs scheduling panel like any other.
//
// WHY ONE CONTROL AND NOT THREE FORMS. Until now a planned repair, a Full Condition Report and a
// site inspection could each be raised ONLY from chat (create_planned_repair, Session ALS;
// create_condition_report and create_site_inspection, Session ALW). Nothing in the browser raised
// any of the three -- so the office could see them on the "Needs scheduling" panel, assign them
// and delete them, but could not create one. Three separate forms would have put three buttons on
// a page for what is, from the office's point of view, one decision taken three ways: "there is
// work at this lift that needs planning in". So the TYPE is the first thing chosen, and only the
// fields that type actually needs are then shown.
//
// IT IS A FRONT END ONLY. Every route it calls already existed, guarded, validated, audited and
// tested. Nothing here re-implements a rule: the retired-lift refusal, the lift-OR-lead exclusive
// choice, the GBP 510 fee, the reuse-instead-of-duplicate recovery and the agreed-price reason
// requirement are all the server's, and this form's job is to state them in the office's own words
// BEFORE the button is pressed rather than to reproduce them.
//
// WHERE IT LIVES, and this was decided by scouting rather than assumed. The brief suggested "near
// the Needs scheduling panel". That panel renders ONLY when something is already waiting
// (SchedulingPage: `needsScheduling.length > 0`), so a control inside it would be invisible in
// exactly the state where raising the first piece of work matters most -- an empty backlog. It
// sits in the page's own action row instead, always visible, beside "New call-out".
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { CalloutListItem, ProspectListItem, RaisedJobResult, ScheduleEngineer } from "../../lib/api";
import { customers as customersResource } from "../../lib/resources";
import { RecordPicker } from "../../components/RecordPicker";
import { erasedPersonRefusal } from "../../lib/erasedPerson";
import type { Customer } from "../../lib/resources";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { SafedRaisePicker } from "./register/safed/SafedRaisePicker";
import { ConfirmButton } from "../../components/ConfirmButton";
import { AccountOnStopLine } from "../../components/AccountSymbol";
import { parseAgreedPrice } from "./calloutRates";
import { CONDITION_REPORT_FEE_GBP } from "../../lib/conditionReportTrigger";
import { gbpExact } from "../../lib/format";
// Session AOC. The technician visit rate the office is about to commit a customer to, from the one
// client mirror, bound to the engine by techVisitRateParity.test.ts. Never re-typed here.
import { TECH_VISIT_DAY_CAP_HOURS, TECH_VISIT_DAY_RATE_LABEL, TECH_VISIT_OVERAGE_HOUR_LABEL, TECH_VISIT_OVERAGE_STEP_MINUTES } from "../../lib/techVisitRate";
// Session AOI. The SAME resumable Customer -> Site -> Asset chain CalloutsPage.tsx's own "New
// customer" call-out intake uses (Session AEP), extracted into a shared hook so the retry-safety
// cannot drift between the two. NEW_LIFT_TYPES is the same free-text lift-type list that form
// offers, imported rather than re-typed for the same reason.
import { useNewCustomerChain, CHAIN_STEP_LABEL } from "../../lib/useNewCustomerChain";
import { ErasedPersonNotice } from "../../components/ErasedPersonNotice";
import { NEW_LIFT_TYPES } from "./CalloutsPage";
import { Button, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { formatDate } from "../../lib/format";

const FIELD = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const CARD = "mb-2 flex cursor-pointer items-start gap-3 rounded-ele border border-ele-purple-accent p-3";

export type RaiseWorkType = "repair" | "condition_report" | "site_inspection" | "tech_visit";

// The four types, in the order the office meets them: the commonest first, the priced survey
// second, the exploratory look third, the tech visit last. Each says what it IS and what it is FOR,
// because picking the wrong one costs a customer GBP 510, costs ELE a survey it should have charged
// for, or (Session ANG) bills a tech visit at the lower repair rate.
const TYPES: { key: RaiseWorkType; label: string; blurb: string }[] = [
  {
    key: "repair",
    label: "Planned repair",
    blurb: "Work ELE already knows needs doing, with no date yet. A customer request, an upgrade, something spotted on site.",
  },
  {
    key: "condition_report",
    label: "Full Condition Report",
    blurb:
      `The priced survey the published Schedule of Fees requires, at GBP ${CONDITION_REPORT_FEE_GBP} ex VAT, where there is no documented ` +
      "evidence of routine maintenance on a lift in the previous 12 months, before any quote or service proceeds.",
  },
  {
    key: "site_inspection",
    label: "Site inspection",
    blurb:
      "A look: a pre-quote survey, an insurance inspection after work, an overview before further work is agreed, or qualifying a lift " +
      "that is not on the register at all. Free unless the office decides otherwise.",
  },
  // Session ANG. It sits AFTER the planned repair it is most easily confused with, and its blurb
  // leads with the one thing that tells them apart -- where the work came from -- because picking
  // the wrong one here is what puts the wrong hourly rate on the weekly invoice.
  {
    key: "tech_visit",
    label: "Tech visit",
    blurb:
      `A return visit a call-out diagnosed as needing more than routine repair work. Charged as a flat day rate, ${TECH_VISIT_DAY_RATE_LABEL} ex VAT ` +
      `covering up to ${TECH_VISIT_DAY_CAP_HOURS} hours on site, in or out of hours and contract, with time beyond that at ${TECH_VISIT_OVERAGE_HOUR_LABEL} an hour, ` +
      `so raise this rather than a planned repair only when a call-out found the fault needed one.`,
  },
];

// The plain-English word for what was raised, and what the office should read afterwards. Pure and
// exported so all three hosts (the Scheduling page, the lift record and the LOLER report page) say
// the same sentence, and so the REUSE case is stated rather than glossed: a repair or a condition
// report that already existed open on the lift is handed back rather than created, and telling the
// office "raised" for that would report work it had not created.
export const RAISE_WORK_WORD: Record<RaiseWorkType, string> = {
  repair: "planned repair",
  condition_report: "Full Condition Report",
  site_inspection: "site inspection",
  tech_visit: "tech visit",
};

export function raisedWorkMessage(result: {
  type: RaiseWorkType;
  reused: boolean;
  originCalloutRef?: string | null;
  assignedTo?: string | null;
  twoManBooked?: boolean;
  twoManRequested?: boolean;
  // Session ANV: the office raised this from a logged call, so the message says whether the call
  // was actually recorded against the work. It matters most on the REUSE branch, where it was not.
  callLogRequested?: boolean;
  // Session AOH: the free-text site a LIFT-LESS planned repair was filed against, as the SERVER
  // echoed it back. Absent/null for every other raise, including a repair against a real lift.
  siteLabel?: string | null;
  // Session ARM (D88): a site inspection raised for an UNKNOWN CALLER echoes whether the server made
  // the caller's sales lead (true) or reused the one an older call already had (false). Absent on
  // every other raise.
  prospectCreated?: boolean;
  // Session ASC (A4): a REUSED planned repair that already carries an approved repair estimate's fixed price, as
  // the server echoed it. Its one invoice is that price, which covers only the estimate's work.
  fixedPrice?: { estimateReference: string | null; agreedPricePence: number } | null;
}): string {
  const word = RAISE_WORK_WORD[result.type];
  if (!result.reused) {
    // Session ANG: a tech visit is the ONE raise type that can carry an engineer at creation, so the
    // unconditional "waiting for a date and an engineer" asserted it was unassigned when it was not.
    const waiting = result.assignedTo ? "waiting for a date" : "waiting for a date and an engineer";
    const fromCall = result.callLogRequested ? " The call it came from is recorded against it." : "";
    // Session AOH: read from the SERVER'S echo (result.siteLabel), never from what the form asked
    // for, so this sentence describes the job that was actually created. It appears only on the
    // lift-less branch, where the office has just been shown no lift field at all and should see
    // confirmed back what the work is filed against instead.
    const noLift = result.siteLabel ? ` It has no lift on the register, so it is recorded against ${result.siteLabel}.` : "";
    // Session ARM: said from the SERVER'S echo too, because the two cases differ and only the server
    // knows which happened (an older call may already have carried a lead).
    const lead =
      result.prospectCreated === true
        ? " The caller is not on the register, so a sales lead was made for them from the call's details."
        : result.prospectCreated === false
          ? " The caller is not on the register, so it is filed against the sales lead this call already had."
          : "";
    return `${word.charAt(0).toUpperCase()}${word.slice(1)} raised. It is on the Needs scheduling panel, ${waiting}.${noLift}${lead}${fromCall}`;
  }
  // Session ANG: a reused TECH VISIT names the call-out the one already open was diagnosed from,
  // because that is the case the reuse hides. If it is a different call-out from the one just
  // chosen, the office is looking at a second fault whose tech visit was never raised, and "already
  // had one" alone would read as "the work is in hand". The route and the MCP tool both report it;
  // until this was added, nothing in the BROWSER did, while this modal's own copy promised it.
  const from = result.type === "tech_visit" && result.originCalloutRef ? ` It came from call-out ${result.originCalloutRef}: if that is not the call-out you meant, that fault still has no tech visit of its own.` : "";
  // Session ANH: a two-man request does not reach a reused job, and the office must not be left
  // believing it did. Said only when it matters: the office asked for two-man and the job is not.
  const booking = result.twoManRequested && result.twoManBooked === false ? " It is NOT booked as a two-man job; record that on the job if it should be." : "";
  // Session ANV: the same honesty the two-man line above exists for. A reused job was raised by
  // something earlier, so the server deliberately does NOT stamp this call onto it, and the office
  // must not be left believing the trail from the call to the work now exists.
  const fromCall = result.callLogRequested
    ? " The call is NOT recorded against it, because the work was already open before this call: note the call in the job if it matters."
    : "";
  // Session ASC (A4): the open repair handed back already carries an approved estimate's fixed price, so the work just
  // described is NOT in its invoice unless the office adds it: said here, where the office learns it was reused.
  const fp = result.type === "repair" && result.fixedPrice && result.fixedPrice.agreedPricePence > 0 ? result.fixedPrice : null;
  const fixed = fp
    ? ` It already carries ${fp.estimateReference ? `repair estimate ${fp.estimateReference}` : "an approved repair estimate"} at a fixed price of ${gbpExact(fp.agreedPricePence / 100)} ex VAT, which covers only that estimate's work: add any other work done on it to its draft invoice by hand.`
    : "";
  return `That lift already had an open ${word}, so it was opened again rather than raising a second one. It is on the Needs scheduling panel.${from}${booking}${fromCall}${fixed}`;
}

// A call-out as the office recognises one: its canonical reference where it has one (the AJF
// CO-ELE-... format), its own jobRef where it does not, then the date and the fault so a list of
// several at one lift can be told apart. Session ANG.
function calloutLabel(c: CalloutListItem): string {
  const ref = c.reference ?? c.ref;
  const when = c.reportedAt ? formatDate(c.reportedAt) : null;
  const fault = c.faultDescription?.trim();
  return [ref, when, fault && fault.length > 60 ? `${fault.slice(0, 60)}...` : fault].filter(Boolean).join(" \u00b7 ");
}

export function RaiseWorkModal({
  preset,
  onClose,
  onRaised,
  onRaisedSafed,
}: {
  // Opened already knowing what to raise and for which lift (the Condition Report prompt on the
  // lift record and the LOLER report page both do this). The office can still change either.
  //
  // Session ANV adds the CALL LOG's own way in. `callLogId` is the logged inbound call this work is
  // being raised from, threaded to whichever of the four routes runs so the job carries the call as
  // its provenance. `customerId`/`customerName` are what the call already knew, and they NARROW the
  // lift picker to that customer's lifts, which is what "pre-filled" can honestly mean on a form
  // whose only target field is a lift: a call log holds no site and no lift, so there is nothing to
  // preselect, but there is a register of 300-odd lifts to cut down to the caller's own.
  //
  // Session ARE (D82) adds a second, parallel way in: `enquiryId`, the Enquiry this work is being
  // raised from (src/routes/office/EnquiryDetailPage.tsx). Additive only, mirroring `callLogId`
  // exactly rather than replacing it -- an enquiry and a logged call are different provenance
  // records that can each raise work, and `customerId`/`customerName` already cover what an enquiry
  // needs to narrow the lift picker the same way a call does.
  //
  // Session ARM (D88) adds `callerLabel`: who rang, as the office would say it (callerLine), for a
  // call with NO customer. When the preset carries a callLogId and no customerId, the site inspection
  // offers a third target, "This caller": the server makes the caller's sales lead from the call (or
  // reuses the one an older call already had) in the same transaction as the inspection, so the
  // office never has to make a lead first.
  preset?: {
    type: RaiseWorkType;
    assetId?: string;
    customerId?: string;
    customerName?: string;
    callLogId?: string;
    enquiryId?: string;
    callerLabel?: string;
    // Session ASM (W-12): the DEFECT this work is raised from ("Raise work" on the defect's own page, then "Log a
    // planned repair"). The repair is then filed against the defect's lift and linked to the defect by the server,
    // exactly as the automatic remedial path links one. The description starts as the defect's own words.
    defectId?: string;
    description?: string;
  };
  onClose: () => void;
  // Called once something was actually raised, so the host reloads. Given the raised job so a host
  // can report it precisely rather than saying "done".
  onRaised: (result: RaisedJobResult & { type: RaiseWorkType; twoManRequested?: boolean; callLogRequested?: boolean }) => void;
  // Session BDC. SAFed testing is its own job type and is raised from the same place as the others. A host that passes this
  // handler gets a fifth card, "SAFed test", which swaps the form for the SAFed raise form (the lift page panel's own form, the
  // same server rules); a host that does not pass it sees the four cards it always saw.
  onRaisedSafed?: (jobId: string | null) => void;
}) {
  const [safed, setSafed] = useState(false);
  const [type, setType] = useState<RaiseWorkType>(preset?.type ?? "repair");
  const [phase, setPhase] = useState<"loading" | "form" | "saving">("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Session SC2A: the lift and the customer are typeahead pickers, so neither register is loaded when the form opens.
  // The customer the chosen lift belongs to is kept from the pick, for the "this customer's lifts only" scope below.
  const [assetCustomerId, setAssetCustomerId] = useState<string | null>(null);
  const [prospects, setProspects] = useState<ProspectListItem[]>([]);
  // Session ANG, both best-effort like the lead list below: a tech visit must stay raiseable when
  // either feed is unavailable, because both fields it drives are OPTIONAL. A failure narrows the
  // form rather than blocking it.
  const [callouts, setCallouts] = useState<CalloutListItem[]>([]);
  // Session AOH: the customer picker for a LIFT-LESS planned repair. Best-effort like the two feeds
  // beside it -- a failure must not block raising an ORDINARY repair against a lift, which is the
  // commonest thing this modal does. customersLoaded distinguishes "no customers" from "the request
  // failed", exactly as calloutsLoaded does below, because the no-lift branch cannot be submitted
  // without one and an empty picker must say which of the two it is.
  const [repairCustomerLabel, setRepairCustomerLabel] = useState<string | null>(null);
  // Whether the call-out feed actually answered, as distinct from answering with nothing. Without
  // it an empty picker cannot tell "this lift has never had a call-out" from "the request failed".
  const [calloutsLoaded, setCalloutsLoaded] = useState(false);
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);

  const [assetId, setAssetId] = useState(preset?.assetId ?? "");
  const [prospectId, setProspectId] = useState("");
  // Session ARM: the unknown caller is a target only when this was opened from a logged call that is
  // not matched to a customer. A matched call's caller IS on the register, so the target would be
  // wrong there, and the server refuses it (409 call_matched).
  const callerTargetAvailable = Boolean(preset?.callLogId) && !preset?.customerId;
  // A site inspection is against a lift OR a sales lead (OR, since Session ARM, the unknown caller on
  // the call it was opened from). The server refuses any two and none, so this is a radio rather than
  // optional pickers: a form that can express a refused state is a form that will produce one. It
  // starts on the caller when that target exists, because an unknown caller's lift is by definition
  // not on the register and they have no lead of their own yet.
  const [target, setTarget] = useState<"lift" | "prospect" | "caller">(callerTargetAvailable ? "caller" : "lift");
  // Session AOH: a planned repair is against a lift on the register OR a customer and a free-text
  // site, for the one-off, non-contract job at a lift ELE does not maintain. A radio for the same
  // reason the inspection's is: the server refuses both and neither, and a form that can express a
  // refused state is a form that will produce one.
  const [repairTarget, setRepairTarget] = useState<"lift" | "no_lift">("lift");
  // Pre-filled from a logged call where there is one: the caller is already known, and this is the
  // one field a call log genuinely CAN preselect (it holds a customer, but no site and no lift).
  const [repairCustomerId, setRepairCustomerId] = useState(preset?.customerId ?? "");
  const [repairSiteLabel, setRepairSiteLabel] = useState("");
  // "Not in the platform?" -- the same inline NON_CONTRACT creation the call-out report form offers
  // (Session AFM), for the same reason: a one-off caller with no record cannot be picked, and
  // sending the office out to the Customers page mid-form loses everything typed so far.
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Session ASQ, Stream G, Item 3: the email typed belongs to a person whose data was erased. The server's own sentence,
  // and "Create anyway" resends with createAnyway (audited).
  const [createErased, setCreateErased] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newContact, setNewContact] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [description, setDescription] = useState(preset?.description ?? "");
  const [reason, setReason] = useState("");
  const [raisedBy, setRaisedBy] = useState("");
  const [originCalloutId, setOriginCalloutId] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  // Session ANH: the office's booking of the job as two-man, which is the ONLY thing that lets the
  // subcontractor rate card price a second engineer's hours on it ("Two-man rates only apply where
  // ELE has booked the job as two-man in advance, tech visits included"). Off by default; sent on
  // every type, since any of the four can be booked two-man.
  const [twoManBooked, setTwoManBooked] = useState(false);
  // The discretionary charge (Session ALW's route, given its first office caller here). Typed in
  // POUNDS because that is what was agreed out loud; parseAgreedPrice converts at the boundary,
  // the same one conversion the call-out log form uses.
  const [priceGbp, setPriceGbp] = useState("");
  const [priceReason, setPriceReason] = useState("");
  // Session ANV. The customer narrowing is a HELP, never a cage: the office can widen it back to the
  // whole register in one click. That escape hatch is not a nicety -- a caller regularly rings about
  // a lift at a block the platform has under a DIFFERENT customer (a managing agent who is not the
  // freeholder on record), and a call from a customer with no lifts at all would otherwise leave the
  // form with an empty picker and no way out of it.
  // Starts WIDE when a specific lift was preset, because narrowing to a customer that lift may not
  // belong to would hide the very lift the caller asked for.
  const [allLifts, setAllLifts] = useState(Boolean(preset?.assetId));

  // Session AOI. A Full Condition Report or a tech visit both genuinely need a real lift record to
  // attach to (a survey's whole purpose is establishing one; a tech visit needs somewhere to hang
  // its diagnosis) -- unlike the planned repair's own no-lift branch above, which files against no
  // lift at all. So instead of a target radio, this is "take the lift's details now" and the
  // resumable chain below creates a real Customer, Site and Asset before the schedule route runs.
  const chain = useNewCustomerChain();
  const [assetMode, setAssetMode] = useState<"existing" | "new">("existing");
  const [newLiftForm, setNewLiftForm] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    siteName: "",
    siteAddress: "",
    eleNumber: "",
    liftType: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The lead list is best-effort: a site inspection against a lift must stay raiseable even if
      // the CRM feed is unavailable, so a failure there narrows the form rather than blocking it.
      // Session SC2B: the call-outs are NOT read here. A tech visit's "Which call-out diagnosed it?" asks for the chosen lift's own
      // call-outs once a lift is picked (the effect below), instead of every call-out the platform holds.
      const [prospectRows, engineerRows] = await Promise.all([
        api.prospects.list().catch(() => [] as ProspectListItem[]),
        api.scheduling.engineers().catch(() => [] as ScheduleEngineer[]),
      ]).catch(() => [null, null] as const);
      if (cancelled) return;
      if (!prospectRows) {
        setLoadError("Could not load the form. Please try again in a moment.");
        setPhase("form");
        return;
      }
      setProspects(prospectRows ?? []);
      setEngineers(engineerRows ?? []);
      setPhase("form");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const busy = phase === "saving";
  const isInspection = type === "site_inspection";
  const isTechVisit = type === "tech_visit";

  // Session SC2B: the chosen lift's call-outs, asked for when a tech visit has a lift (GET /api/callouts?assetId=), never the whole list.
  useEffect(() => {
    setCallouts([]);
    setCalloutsLoaded(false);
    if (!isTechVisit || !assetId) return;
    let cancelled = false;
    api.callouts
      .list({ assetId })
      .then((rows) => {
        if (cancelled) return;
        setCallouts(rows);
        setCalloutsLoaded(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isTechVisit, assetId]);
  // Session AOH. Only a planned repair offers the NO-LIFT branch (no lift at all, filed against a
  // free-text site): a site inspection already has its own no-lift path in the sales lead. A Full
  // Condition Report and a tech visit stay LIFT-REQUIRED -- both are priced per lift and a tech
  // visit is justified by a call-out at one -- but Session AOI adds a second way to satisfy that:
  // pick an existing lift, or take its details now and create one (creatingNewLift below).
  const repairNoLift = type === "repair" && repairTarget === "no_lift";
  // Session AOI: "the lift is not on the register yet", offered only where the work genuinely needs
  // a real lift record (a condition report establishes one; a tech visit hangs its diagnosis on
  // one) -- never on a repair (its own no-lift path above files against no lift at all) or an
  // inspection (its own no-lift path is a sales lead, not a lift that does not exist yet).
  const creatingNewLift = (type === "condition_report" || isTechVisit) && assetMode === "new";
  const needsLift = isInspection ? target === "lift" : !repairNoLift;
  // Only this lift's call-outs, newest first. A tech visit is BY DEFINITION diagnosed on a call-out
  // at this lift, so offering the whole register would be offering a list that is wrong except for
  // a handful of rows -- and the wrong one would attach this visit's justification to another
  // building. Choosing the lift first is what makes the list short enough to be read.
  const liftCallouts = useMemo(
    () =>
      callouts
        .filter((c) => c.assetId != null && c.assetId === assetId)
        .sort((a, b) => (b.reportedAt ?? "").localeCompare(a.reportedAt ?? "")),
    [callouts, assetId],
  );
  // Session ANV: when this was opened from a logged call, the lift picker is narrowed to the caller's own lifts (the
  // picker's customer filter), with a way out to every lift. Session SC2A: the lifts are searched, not preloaded.
  const narrowed = Boolean(preset?.customerId) && !allLifts;

  // Widening or narrowing the picker CLEARS a chosen lift that the new list does not contain, for
  // exactly the reason Session ANG's lift-change handler below does: a select whose value matches no
  // option renders BLANK while React state still holds it, and it is still what submits. The office
  // would see an empty field and raise work against a lift it could no longer see.
  function setLiftScope(showAll: boolean) {
    setAllLifts(showAll);
    // Narrowing clears a chosen lift that belongs to another customer (the new filter would not offer it).
    if (!showAll && assetId !== "" && assetCustomerId !== null && assetCustomerId !== preset?.customerId) {
      setAssetId("");
      setOriginCalloutId("");
    }
  }

  // Session AOI. Switching to "take the details now" clears any lift already chosen, for the SAME
  // reason setLiftScope above does: a stale assetId would otherwise still drive the tech visit's
  // "Which call-out diagnosed it?" picker, showing that OTHER lift's history against a visit that
  // is about to be raised for a lift which does not exist yet.
  function setAssetModeAndClear(mode: "existing" | "new") {
    setAssetMode(mode);
    if (mode === "new") {
      setAssetId("");
      setOriginCalloutId("");
    }
    setError(null);
  }

  const agreed = useMemo(() => parseAgreedPrice(priceGbp), [priceGbp]);
  // A valid figure -- and that includes exactly zero, "attended, waived", which is a real recorded
  // decision rather than an empty field -- must carry a reason. The server enforces the identical
  // rule (agreedPriceReasonError); this is the client half of a defence in depth, so the office is
  // told while typing rather than by a 400 after pressing the button.
  const priceReasonMissing = agreed.state === "ok" && priceReason.trim().length === 0;

  // Session AOI: the three names are what the new records need; the ELE number and lift type are
  // genuinely optional, matching CalloutsPage.tsx's own canLogNew. A record already committed by a
  // failed attempt no longer needs its field filled, so this reads what the chain has already
  // created as well as what is typed, which is what lets the office retry after a partial failure.
  const canCreateNewLift = Boolean(
    (chain.created.customer || newLiftForm.customerName.trim()) &&
      (chain.created.site || (newLiftForm.siteName.trim() && newLiftForm.siteAddress.trim())),
  );

  const canSubmit =
    !busy &&
    // Session AOH: three targets now, not two. A no-lift repair needs BOTH the customer and the
    // site, which is the same pair the route refuses without -- stated here so the office is told
    // while filling the form rather than by a 400 after pressing the button.
    // Session AOI adds a FOURTH: a condition report / tech visit taking a new lift's details needs
    // the customer and site names typed (or already committed by a previous, partial attempt).
    (creatingNewLift
      ? canCreateNewLift
      : needsLift
        ? assetId !== ""
        : repairNoLift
          ? repairCustomerId !== "" && repairSiteLabel.trim() !== ""
          : isInspection && target === "caller"
            ? callerTargetAvailable
            : prospectId !== "") &&
    // Both the planned repair and the tech visit require a description: the server does too, and a
    // tech visit with no record of what the call-out diagnosed is a day rate above repair labour that
    // nobody can later justify.
    ((type !== "repair" && type !== "tech_visit") || description.trim() !== "") &&
    (!isInspection || reason.trim() !== "") &&
    (!isInspection || (agreed.state !== "invalid" && !priceReasonMissing));

  // Session AOH: "Not in the platform?" -- create a real NON_CONTRACT Customer (the server also
  // creates its Site and attempts the Xero contact), then carry on with it selected, exactly as the
  // call-out report form's own inline creation does (Session AFM). The record is what makes this
  // caller findable, promotable and cleanly invoiced on every future job, so it is a real customer
  // from the first minute rather than a name typed into a free-text field.
  //
  // The site or address just typed PRE-FILLS the repair's own site label, because on this path they
  // are the same place and asking twice invites them to disagree. It stays editable: the customer's
  // registered address and the address the lift is at are not always the same thing.
  async function createOneOffCustomer(createAnyway = false) {
    const name = newName.trim();
    const siteAddress = newAddress.trim();
    if (!name || !siteAddress) return;
    setCreateBusy(true);
    setCreateError(null);
    setCreateErased(null);
    try {
      const created = (await customersResource.create({
        name,
        customerType: "NON_CONTRACT",
        siteAddress,
        billingContact: newContact.trim() || undefined,
        billingEmail: newEmail.trim() || undefined,
        phone: newPhone.trim() || undefined,
        ...(createAnyway ? { createAnyway: true } : {}),
      })) as Customer;
      setRepairCustomerLabel(created.name);
      setRepairCustomerId(created.id);
      setRepairSiteLabel((v) => v.trim() || siteAddress);
      setCreateOpen(false);
      setError(null);
    } catch (err) {
      const erased = erasedPersonRefusal(err);
      if (erased) setCreateErased(erased.message);
      else setCreateError(errText(err, "Could not create that customer. Please try again."));
    } finally {
      setCreateBusy(false);
    }
  }

  async function submit() {
    if (!canSubmit) return;
    setError(null);
    setPhase("saving");
    const by = raisedBy.trim() || undefined;
    const desc = description.trim() || undefined;
    // Sent only when ticked, so a caller reading the request sees the booking as a stated fact
    // rather than a default that happens to be false.
    const twoMan = twoManBooked ? { twoManBooked: true as const } : {};
    // Session ANV: sent only when this really was opened from a call, so every other caller's
    // request body is byte-identical to its pre-ANV shape.
    const fromCall = preset?.callLogId ? { callLogId: preset.callLogId } : {};
    // Session ARE (D82): the enquiry sibling of fromCall above, same reasoning, sent only when this
    // really was opened from one. `enquiryId` is not yet a declared field on these four request body
    // types in src/lib/api.ts (owned by the parallel backend session building the Enquiry routes, not
    // edited here) -- spreading it in rather than widening those signatures is deliberate, matches
    // how `twoMan` above is already merged in the same way, and keeps every other caller's request
    // body byte-identical.
    const fromEnquiry = preset?.enquiryId ? { enquiryId: preset.enquiryId } : {};
    // Session ASM (W-12): sent only on a lift repair raised from a defect; every other request is byte-identical.
    const fromDefect = preset?.defectId && type === "repair" && !repairNoLift ? { defectId: preset.defectId } : {};
    try {
      // Session AOI: a condition report / tech visit taking a new lift's details creates the
      // Customer, Site and Asset FIRST (resuming anything a previous attempt already committed),
      // then carries straight on exactly as though that lift had been chosen from the picker.
      // Never throws: a failed step is reported through chain.chainFailure and this returns early,
      // leaving the phase back on "form" so the office sees exactly what landed and can retry.
      let resolvedAssetId = assetId;
      if (creatingNewLift) {
        const newAssetId = await chain.run({
          customerName: newLiftForm.customerName,
          customerEmail: newLiftForm.customerEmail,
          customerPhone: newLiftForm.customerPhone,
          customerType: "NON_CONTRACT",
          siteName: newLiftForm.siteName,
          siteAddress: newLiftForm.siteAddress,
          eleNumber: newLiftForm.eleNumber,
          liftType: newLiftForm.liftType,
        });
        if (!newAssetId) {
          setPhase("form");
          return;
        }
        resolvedAssetId = newAssetId;
      }
      let result: RaisedJobResult;
      if (type === "repair") {
        result = await api.schedule.plannedRepair({
          // Session AOH: exactly one of the two shapes the route accepts. Spread rather than sent as
          // undefined fields, so an ordinary repair's request body is byte-identical to its pre-AOH
          // form and the route's "not both" refinement can never be tripped by an empty string.
          ...(repairNoLift ? { customerId: repairCustomerId, siteLabel: repairSiteLabel.trim() } : { assetId }),
          description: description.trim(),
          ...(by ? { raisedBy: by } : {}),
          ...twoMan,
          ...fromCall,
          ...fromEnquiry,
          ...fromDefect,
        });
      } else if (type === "condition_report") {
        result = await api.schedule.conditionReport({ assetId: resolvedAssetId, ...(desc ? { description: desc } : {}), ...(by ? { raisedBy: by } : {}), ...twoMan, ...fromCall, ...fromEnquiry });
      } else if (type === "tech_visit") {
        result = await api.schedule.techVisit({
          assetId: resolvedAssetId,
          description: description.trim(),
          ...(originCalloutId ? { originCalloutId } : {}),
          ...(assignedTo ? { assignedTo } : {}),
          ...(by ? { raisedBy: by } : {}),
          ...twoMan,
          ...fromCall,
          ...fromEnquiry,
        });
      } else if (type === "site_inspection") {
        result = await api.schedule.siteInspection({
          // Session ARM: the caller target sends no lift and no lead, only leadFromCall beside the
          // callLogId fromCall already carries; the server makes (or reuses) the lead itself.
          ...(target === "lift" ? { assetId } : target === "caller" ? { leadFromCall: true as const } : { prospectId }),
          reason: reason.trim(),
          ...(desc ? { description: desc } : {}),
          ...(by ? { raisedBy: by } : {}),
          ...twoMan,
          ...fromCall,
          ...fromEnquiry,
        });
        // The charge is a SECOND, separate write against the inspection's own route, not a field on
        // the create. Two reasons, both deliberate: the create route has never accepted a price and
        // giving it one would be new server logic in a front-end session, and the charge carries its
        // own audit entry (inspection_charge_set), which is what an office decision about money
        // should leave behind. If it fails, the inspection still exists and the office is told
        // exactly that, rather than being left to guess whether anything landed.
        if (agreed.state === "ok") {
          try {
            await api.schedule.inspectionCharge(result.jobId, {
              agreedPricePence: agreed.pence,
              agreedPriceReason: priceReason.trim(),
            });
          } catch (chargeErr) {
            onRaised({ ...result, type, callLogRequested: Boolean(preset?.callLogId) });
            setError(
              `The site inspection was raised, but its charge could not be set: ${errText(chargeErr, "the charge was not saved")}. ` +
                "Set it from the visit on the Scheduling page.",
            );
            setPhase("form");
            return;
          }
        }
      } else {
        // Session ANG made this branch UNREACHABLE-BY-CONSTRUCTION rather than a catch-all, and the
        // change is the point. It used to be a bare `else` holding the site-inspection call, so a
        // new RaiseWorkType with no branch of its own did not fail: it silently POSTed a SITE
        // INSPECTION, with this modal's success message then naming whichever type the office had
        // actually picked. `never` makes the compiler find the missing branch instead.
        const unreachable: never = type;
        throw new Error(`No submit branch for raise type ${String(unreachable)}`);
      }
      // `result` already carries the server's own customerId/siteLabel echo (Session AOH), so
      // raisedWorkMessage reads what was CREATED rather than what this form asked for.
      onRaised({ ...result, type, twoManRequested: twoManBooked, callLogRequested: Boolean(preset?.callLogId) });
    } catch (err) {
      setError(errText(err, "Could not raise this work. Please try again."));
      setPhase("form");
    }
  }

  return (
    <ModalShell ariaLabel="Raise work" onClose={() => !busy && onClose()} width="max-w-[560px]" panelClassName="p-6 max-h-[85vh] overflow-y-auto">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-ele-strong">Raise work</h2>
          <p className="text-sm text-ele-body">It lands on the Needs scheduling panel with no date and no engineer, for you to plan in.</p>
          {preset?.defectId && type === "repair" && (
            <p role="status" className="mt-1 text-sm text-ele-strong">This repair is raised from the defect and will be linked to it.</p>
          )}
        </div>
        <button
          onClick={() => !busy && onClose()}
          aria-label="Close"
          className="-mr-2 -mt-2 flex min-h-11 min-w-11 items-center justify-center rounded-ele text-ele-body hover:text-ele-green"
        >
          ✕
        </button>
      </div>

      {phase === "loading" ? (
        <Loader shape="inline" />
      ) : (
        // onSubmit is a hard no-op and the two-step ConfirmButton below is the ONLY way to fire
        // this, exactly as the call-out log form's New customer mode is: raising a condition
        // report commits a customer to a GBP 510 charge, which is not something a stray Enter key
        // in a text box should do.
        <form onSubmit={(e) => e.preventDefault()}>
          <fieldset className="mb-4" disabled={busy}>
            <legend className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">What are you raising?</legend>
            {TYPES.map((t) => (
              <label key={t.key} className={CARD}>
                <input
                  type="radio"
                  name="raise-type"
                  value={t.key}
                  checked={!safed && type === t.key}
                  onChange={() => {
                    setSafed(false);
                    setType(t.key);
                    setError(null);
                  }}
                  className="mt-1 accent-ele-green"
                />
                <span className="text-sm text-ele-strong">
                  {t.label}
                  <span className="block text-ele-body">{t.blurb}</span>
                </span>
              </label>
            ))}
            {onRaisedSafed && (
              <label className={CARD}>
                <input type="radio" name="raise-type" value="safed_test" checked={safed} onChange={() => { setSafed(true); setError(null); }} className="mt-1 accent-ele-green" />
                <span className="text-sm text-ele-strong">
                  SAFed test
                  <span className="block text-ele-body">
                    Supplementary testing of a lift, from an examiner&apos;s report or the customer&apos;s own request: its own job type. Pick the lift, the tests, and where it was called for.
                  </span>
                </span>
              </label>
            )}
          </fieldset>

          {safed && onRaisedSafed && (
            <SafedRaisePicker onRaised={(jobId) => onRaisedSafed(jobId)} onCancel={onClose} />
          )}

          {!safed && (<>
          {loadError && (
            <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {loadError}
            </p>
          )}

          {/* Session AOH: the planned repair's own target choice, in the same place and the same shape
              as the inspection's below, because it answers the same question. "On the register" is
              today's flow and stays the default: a customer ELE already maintains should still have
              their repair filed against the actual lift. */}
          {type === "repair" && (
            <fieldset className="mb-4" disabled={busy}>
              <legend className={LABEL}>Where is this repair?</legend>
              <label className="mr-4 inline-flex items-center gap-2 text-sm text-ele-strong">
                <input
                  type="radio"
                  name="raise-repair-target"
                  checked={repairTarget === "lift"}
                  onChange={() => {
                    setRepairTarget("lift");
                    setError(null);
                  }}
                  className="accent-ele-green"
                />
                A lift on the register
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-ele-strong">
                <input
                  type="radio"
                  name="raise-repair-target"
                  checked={repairTarget === "no_lift"}
                  onChange={() => {
                    setRepairTarget("no_lift");
                    setError(null);
                  }}
                  className="accent-ele-blue"
                />
                One-off, no lift registered
              </label>
            </fieldset>
          )}

          {isInspection && (
            <fieldset className="mb-4" disabled={busy}>
              <legend className={LABEL}>Who or what is this inspection for?</legend>
              {/* Session ARM: "This caller" first when it exists, since it is the default there. */}
              {callerTargetAvailable && (
                <label className="mr-4 inline-flex items-center gap-2 text-sm text-ele-strong">
                  <input type="radio" name="raise-target" checked={target === "caller"} onChange={() => setTarget("caller")} className="accent-ele-green" />
                  This caller
                </label>
              )}
              <label className="mr-4 inline-flex items-center gap-2 text-sm text-ele-strong">
                <input type="radio" name="raise-target" checked={target === "lift"} onChange={() => setTarget("lift")} className="accent-ele-green" />
                A lift on the register
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-ele-strong">
                <input type="radio" name="raise-target" checked={target === "prospect"} onChange={() => setTarget("prospect")} className="accent-ele-blue" />
                A sales lead
              </label>
            </fieldset>
          )}

          {/* Session AOI: a condition report or a tech visit both need a real lift record, so this
              is not a third no-lift path like the repair's or the inspection's above -- it is a
              second way to GET a lift: pick one from the register, or take its details now and
              create one below. */}
          {(type === "condition_report" || isTechVisit) && (
            <fieldset className="mb-4" disabled={busy}>
              <legend className={LABEL}>Lift not on the register yet?</legend>
              <label className="mr-4 inline-flex items-center gap-2 text-sm text-ele-strong">
                <input
                  type="radio"
                  name="raise-asset-mode"
                  checked={assetMode === "existing"}
                  onChange={() => setAssetModeAndClear("existing")}
                  // A record already created cannot be abandoned by flipping back to the picker,
                  // matching CalloutsPage.tsx's own New customer toggle: the office finishes (or
                  // leaves) the chain it started, so nothing is stranded.
                  disabled={Boolean(chain.created.customer)}
                  className="accent-ele-green"
                />
                On the register
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-ele-strong">
                <input
                  type="radio"
                  name="raise-asset-mode"
                  checked={assetMode === "new"}
                  onChange={() => setAssetModeAndClear("new")}
                  className="accent-ele-blue"
                />
                Not yet
              </label>
              {assetMode === "new" && (
                <p className="mt-1 text-xs text-ele-body">
                  Type the customer, site and lift details below. They are created as real records on the registers before the {RAISE_WORK_WORD[type]} is
                  raised.
                </p>
              )}
            </fieldset>
          )}

          {creatingNewLift ? (
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              {/* autoComplete off: these capture the CUSTOMER's details, so the operator's own saved
                  name/email must never be autofilled into them (as the Emergency call-out form). */}
              <div>
                <label className={LABEL} htmlFor="raise-nc-name">
                  Customer name
                </label>
                <input
                  id="raise-nc-name"
                  autoComplete="off"
                  value={chain.created.customer ? chain.created.customer.name : newLiftForm.customerName}
                  disabled={busy || Boolean(chain.created.customer)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, customerName: e.target.value }))}
                  className={FIELD}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="raise-nc-email">
                  Email address
                </label>
                <input
                  id="raise-nc-email"
                  type="email"
                  autoComplete="off"
                  value={newLiftForm.customerEmail}
                  disabled={busy || Boolean(chain.created.customer)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, customerEmail: e.target.value }))}
                  className={FIELD}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="raise-nc-phone">
                  Phone number
                </label>
                <input
                  id="raise-nc-phone"
                  autoComplete="off"
                  value={newLiftForm.customerPhone}
                  disabled={busy || Boolean(chain.created.customer)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, customerPhone: e.target.value }))}
                  className={FIELD}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="raise-nc-site-name">
                  Site name
                </label>
                <input
                  id="raise-nc-site-name"
                  autoComplete="off"
                  value={chain.created.site ? chain.created.site.name : newLiftForm.siteName}
                  disabled={busy || Boolean(chain.created.site)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, siteName: e.target.value }))}
                  placeholder="e.g. Rosina Court"
                  className={FIELD}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL} htmlFor="raise-nc-site-address">
                  Site address
                </label>
                <input
                  id="raise-nc-site-address"
                  autoComplete="off"
                  value={newLiftForm.siteAddress}
                  disabled={busy || Boolean(chain.created.site)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, siteAddress: e.target.value }))}
                  className={FIELD}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="raise-nc-ele">
                  Lift ID (ELE number)
                </label>
                <input
                  id="raise-nc-ele"
                  autoComplete="off"
                  value={newLiftForm.eleNumber}
                  disabled={busy || Boolean(chain.created.asset)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, eleNumber: e.target.value }))}
                  className={FIELD}
                />
                <p className="mt-1 text-xs text-ele-body">Leave blank if it does not have one yet. ELE numbers are allocated at signing.</p>
              </div>
              <div>
                <label className={LABEL} htmlFor="raise-nc-lift-type">
                  Lift type
                </label>
                <select
                  id="raise-nc-lift-type"
                  value={newLiftForm.liftType}
                  disabled={busy || Boolean(chain.created.asset)}
                  onChange={(e) => setNewLiftForm((f) => ({ ...f, liftType: e.target.value }))}
                  className={FIELD}
                >
                  {NEW_LIFT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : needsLift ? (
            <div className="mb-4">
              <RecordPicker
                kind="lift"
                id="raise-asset"
                label="Lift"
                value={assetId}
                placeholder="Search by ELE number, serial, site or postcode"
                disabled={busy}
                filters={{ includeRetired: false, ...(narrowed && preset?.customerId ? { customerId: preset.customerId } : {}) }}
                onChange={(id, rec) => {
                  setAssetId(id);
                  setAssetCustomerId((rec?.raw as { site?: { customer?: { id?: string } } } | undefined)?.site?.customer?.id ?? null);
                  // Session ANG: changing the lift CLEARS any call-out already chosen. The picker
                  // below is filtered to the selected lift, so after a change the old id matches no
                  // option and the select renders BLANK -- while React state still holds it, and it
                  // is still what submits. The server refuses that outright (409 callout_other_lift);
                  // this is the half that stops the office ever meeting the refusal.
                  setOriginCalloutId("");
                }}
              />
              {/* Session ANV: what the narrowing is doing, and the way out of it, both said plainly.
                  A filter the office cannot see is a filter it will blame the register for. */}
              {preset?.customerId && (
                <p className="mt-1 text-xs text-ele-body">
                  {narrowed ? (
                    <>
                      Showing {preset.customerName ?? "the caller"}&apos;s lifts only.{" "}
                      <button type="button" onClick={() => setLiftScope(true)} className="font-menu tracking-wide text-ele-green underline-offset-2 hover:underline">
                        Show every lift
                      </button>
                    </>
                  ) : (
                    <>
                      Showing every lift on the register.{" "}
                      <button type="button" onClick={() => setLiftScope(false)} className="font-menu tracking-wide text-ele-green underline-offset-2 hover:underline">
                        Show {preset.customerName ?? "the caller"}&apos;s lifts only
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
          ) : repairNoLift ? (
            /* Session AOH: the one-off, non-contract repair. The customer and the site are BOTH
               required -- a Job carries no free-text customer name of its own (see Job.customerId),
               so without the customer nothing can be invoiced or chased, and without the site nobody
               can be sent. The lift picker is replaced rather than added to, so the form can never
               submit a shape the route refuses. */
            <>
              <div className="mb-4">
                <RecordPicker
                  kind="customer"
                  id="raise-repair-customer"
                  label="Customer"
                  value={repairCustomerId}
                  selectedLabel={repairCustomerLabel}
                  placeholder="Search for a customer"
                  disabled={busy}
                  filters={{ customerType: "all" }}
                  onChange={(id, rec) => {
                    setRepairCustomerId(id);
                    setRepairCustomerLabel(rec?.label ?? null);
                    setError(null);
                  }}
                />
                <p className="mt-1 text-xs text-ele-body">
                  The job is filed against this customer, so it can be found, chased and invoiced later. There is no lift record to hang it on.
                </p>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setCreateOpen((o) => !o)}
                    disabled={busy}
                    className="font-menu text-xs tracking-wide text-ele-body underline-offset-2 hover:text-ele-strong hover:underline"
                  >
                    Not in the platform?
                  </button>
                  {createOpen && (
                    <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-inset p-3">
                      <p className="mb-2 text-xs text-ele-body">
                        Create a one-off caller record. They are billed at non-contract rates, kept out of the main customer list, and can be
                        promoted to a full customer later.
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className={LABEL} htmlFor="raise-new-name">
                            Company / caller name*
                          </label>
                          <input id="raise-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} disabled={createBusy} className={FIELD} />
                        </div>
                        <div>
                          <label className={LABEL} htmlFor="raise-new-address">
                            Site address*
                          </label>
                          <input
                            id="raise-new-address"
                            value={newAddress}
                            onChange={(e) => setNewAddress(e.target.value)}
                            disabled={createBusy}
                            className={FIELD}
                          />
                        </div>
                        <div>
                          <label className={LABEL} htmlFor="raise-new-contact">
                            Contact name
                          </label>
                          <input
                            id="raise-new-contact"
                            value={newContact}
                            onChange={(e) => setNewContact(e.target.value)}
                            disabled={createBusy}
                            className={FIELD}
                          />
                        </div>
                        <div>
                          <label className={LABEL} htmlFor="raise-new-email">
                            Email
                          </label>
                          <input id="raise-new-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} disabled={createBusy} className={FIELD} />
                        </div>
                        <div>
                          <label className={LABEL} htmlFor="raise-new-phone">
                            Phone
                          </label>
                          <input id="raise-new-phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} disabled={createBusy} className={FIELD} />
                        </div>
                      </div>
                      {createError && (
                        <p role="alert" className="mt-2 text-sm text-ele-strong">
                          {createError}
                        </p>
                      )}
                      {createErased && (
                        <div role="alert" className="mt-2 rounded-ele border border-amber-400 p-2 text-sm text-ele-strong">
                          <p>{createErased}</p>
                          <button
                            type="button"
                            onClick={() => void createOneOffCustomer(true)}
                            disabled={createBusy}
                            className="mt-2 rounded-ele border border-ele-danger px-3 py-1.5 font-heading text-xs tracking-wide text-ele-danger disabled:opacity-50"
                          >
                            {createBusy ? "Creating..." : "Create anyway"}
                          </button>
                        </div>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => void createOneOffCustomer()}
                        disabled={createBusy || newName.trim() === "" || newAddress.trim() === ""}
                      >
                        {createBusy ? "Creating..." : "Create caller"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className={LABEL} htmlFor="raise-repair-site">
                  Site or address
                </label>
                <input
                  id="raise-repair-site"
                  value={repairSiteLabel}
                  onChange={(e) => {
                    setRepairSiteLabel(e.target.value);
                    setError(null);
                  }}
                  disabled={busy}
                  placeholder="e.g. Bridge House, 42 Wharf Road, E14"
                  className={FIELD}
                />
                <p className="mt-1 text-xs text-ele-body">
                  Where the engineer is going, in your own words. It is the only location this job has, so write what somebody who has never been
                  there would need.
                </p>
              </div>
            </>
          ) : isInspection && target === "caller" ? (
            /* Session ARM (D88): no picker, because there is nothing to pick. The lead is made from
               the call when the inspection is raised, in the same write, or the one this call
               already has is used; saying so here is what stops the office making one first. */
            <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
              <p className="text-ele-strong">{preset?.callerLabel?.trim() || "The caller on this call"}</p>
              <p className="mt-1">
                They are not on the register. When you raise this, a sales lead is made for them from the call&apos;s details (their name, number and
                what they rang about), the inspection is filed against it, and the call is recorded against the inspection. If this call already has
                a sales lead, that one is used instead of making a second.
              </p>
            </div>
          ) : (
            <div className="mb-4">
              <label className={LABEL} htmlFor="raise-prospect">
                Sales lead
              </label>
              <select id="raise-prospect" value={prospectId} onChange={(e) => setProspectId(e.target.value)} disabled={busy} className={FIELD}>
                <option value="">Choose a sales lead</option>
                {prospects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.company}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ele-body">
                For a lift that is not on the register at all. There is no lift record to attach the visit to, so the report is filed against the lead.
              </p>
            </div>
          )}

          {isInspection && (
            <div className="mb-4">
              <label className={LABEL} htmlFor="raise-reason">
                Why is this inspection happening?
              </label>
              <input
                id="raise-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                placeholder="e.g. Insurance inspection after the controller replacement"
                className={FIELD}
              />
              <p className="mt-1 text-xs text-ele-body">
                It is the only thing that says whether this is a pre-quote survey or an insurance check, and the two are not the same visit.
              </p>
            </div>
          )}

          {isTechVisit && (
            <>
              <div className="mb-4">
                <label className={LABEL} htmlFor="raise-callout">
                  Which call-out diagnosed it?
                </label>
                <select
                  id="raise-callout"
                  value={originCalloutId}
                  onChange={(e) => setOriginCalloutId(e.target.value)}
                  disabled={busy || assetId === ""}
                  className={FIELD}
                >
                  <option value="">{assetId === "" ? "Choose a lift first" : "Not recorded"}</option>
                  {liftCallouts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {calloutLabel(c)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ele-body">
                  {/* Session ANG: an empty list is only a fact about the LIFT when the feed actually
                      loaded. It is fetched best-effort (a .catch that yields []), so reporting a
                      failed load as "this lift has no call-outs on record" would have the form
                      asserting something false about the history, on the field that justifies the
                      higher rate. calloutsLoaded distinguishes the two. */}
                  {assetId !== "" && liftCallouts.length === 0
                    ? calloutsLoaded
                      ? "This lift has no call-outs on record, so there is nothing to link. You can still raise the visit."
                      : "The call-out list could not be loaded, so there is nothing to choose from. You can still raise the visit and record the call-out in the notes."
                    : "It is the record of why this visit is charged above the repair rate, and the first thing anyone querying the invoice will ask for."}
                </p>
              </div>

              <div className="mb-4">
                <label className={LABEL} htmlFor="raise-engineer">
                  Engineer (optional)
                </label>
                <select id="raise-engineer" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} disabled={busy} className={FIELD}>
                  <option value="">Leave unassigned</option>
                  {engineers.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ele-body">
                  ELE's own cost depends on who attends (the customer's day rate does not), and a tech visit normally goes to whoever diagnosed it. Leave it blank to decide when you plan it in.
                </p>
              </div>
            </>
          )}

          <div className="mb-4">
            <label className={LABEL} htmlFor="raise-description">
              {type === "repair"
                ? "What work is needed?"
                : isTechVisit
                  ? "What did the call-out find, and what does the tech visit need to do?"
                  : "Notes (optional)"}
            </label>
            <textarea
              id="raise-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
              rows={3}
              placeholder={
                type === "repair"
                  ? "e.g. Replace the landing door closer on the second floor"
                  : type === "condition_report"
                    ? "Left blank, the standard Schedule of Fees wording is recorded."
                    : isTechVisit
                      ? "e.g. Intermittent door fault traced to the controller; needs a drive test rig and a second visit"
                      : "Anything the engineer should know before attending."
              }
              className={FIELD}
            />
          </div>

          {type === "condition_report" && (
            <p className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
              Charged at the locked GBP {CONDITION_REPORT_FEE_GBP} ex VAT, as a Xero DRAFT invoice raised when the completed report is approved. Never
              authorised automatically. If this lift already has an open condition report, this opens that one again rather than raising a second charge.
            </p>
          )}

          {isTechVisit && (
            <p className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
              Raising it here IS the office approval a tech visit needs before it is booked: an engineer cannot raise one from their phone. It lands with
              no date, for you to plan in. If this lift already has an open tech visit, this opens that one again rather than raising a second day rate.
              Charged as a{" "}
              <strong className="text-ele-strong">
                flat day rate, {TECH_VISIT_DAY_RATE_LABEL} ex VAT, covering up to {TECH_VISIT_DAY_CAP_HOURS} hours on site
              </strong>
              , in or out of hours and in or out of contract, at every cover tier: a cover tier's call-out discount does not reduce it. Time on site beyond the {TECH_VISIT_DAY_CAP_HOURS} hours is also charged, at {TECH_VISIT_OVERAGE_HOUR_LABEL} an hour in {TECH_VISIT_OVERAGE_STEP_MINUTES}-minute steps rounded up, taken from the
              arrival and departure on the job sheet, or as a further day if you choose that on the visit once it is booked (a further day covers a further {TECH_VISIT_DAY_CAP_HOURS} hours, and time beyond that is hourly again). A Xero DRAFT invoice is raised when you approve the completed visit, with the day rate and any
              time beyond the {TECH_VISIT_DAY_CAP_HOURS} hours as a second line. Never authorised automatically, so add any parts to it first, but do not add the hours again.
            </p>
          )}

          {/* Session AMF item 3: the site inspection charge, from the office UI for the first time.
              A site inspection has NO standard rate and none is invented here: the office decides
              per visit, and the three states are stated in the office's own words because the
              difference between them is real money. Blank is not the same as zero. */}
          {isInspection && (
            <fieldset className="mb-4" disabled={busy}>
              <legend className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Charge (optional)</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={LABEL} htmlFor="raise-price">
                    Agreed price (GBP ex VAT)
                  </label>
                  <input
                    id="raise-price"
                    inputMode="decimal"
                    value={priceGbp}
                    onChange={(e) => {
                      setPriceGbp(e.target.value);
                      setError(null);
                    }}
                    placeholder="Leave blank for no charge"
                    className={FIELD}
                  />
                  {agreed.state === "invalid" && (
                    <p role="alert" className="mt-1 text-xs text-ele-strong">
                      {agreed.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className={LABEL} htmlFor="raise-price-reason">
                    Reason for the price
                  </label>
                  <input
                    id="raise-price-reason"
                    value={priceReason}
                    onChange={(e) => {
                      setPriceReason(e.target.value);
                      setError(null);
                    }}
                    disabled={agreed.state !== "ok"}
                    placeholder="e.g. Agreed with the managing agent on the phone"
                    className={FIELD}
                  />
                  {priceReasonMissing && (
                    <p role="alert" className="mt-1 text-xs text-ele-strong">
                      Enter a reason for the agreed price. It is the only record of why this visit was priced the way it was.
                    </p>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-ele-body">
                Blank means the visit is not priced and nothing is invoiced. <strong className="text-ele-strong">0</strong> is different: it records the
                deliberate decision that ELE attended and waived the charge. Any figure above zero raises a Xero DRAFT invoice when the visit is approved,
                never an authorised one.
              </p>
            </fieldset>
          )}

          <div className="mb-4 flex items-start gap-3 text-sm">
            <input
              id="raise-two-man"
              type="checkbox"
              checked={twoManBooked}
              onChange={(e) => setTwoManBooked(e.target.checked)}
              disabled={busy}
              className="mt-1 accent-ele-green"
              aria-describedby="raise-two-man-help"
            />
            <span>
              <label htmlFor="raise-two-man" className="text-ele-strong">
                Booked as a two-man job
              </label>
              <span id="raise-two-man-help" className="block text-xs text-ele-body">
                Tick this only if ELE is sending two engineers. Under the subcontractor rate card a second engineer is chargeable only on a job booked two-man in
                advance; a second engineer who turns up on a job booked single-man is flagged on the timesheet for review rather than priced.
              </span>
            </span>
          </div>

          <div className="mb-4">
            <label className={LABEL} htmlFor="raise-by">
              Raised by (optional)
            </label>
            <input
              id="raise-by"
              value={raisedBy}
              onChange={(e) => setRaisedBy(e.target.value)}
              disabled={busy}
              placeholder="Who asked for this, or where it came from"
              className={FIELD}
            />
          </div>

          {/* Session AOI. Anything that went wrong AFTER a record was created, mirroring
              CalloutsPage.tsx's own panel: the chain stopping part way, OR the chain finishing but
              the schedule call itself failing. The second is the quieter one -- the records are
              just as real, and without this the office would be told only "could not raise this
              work" while a customer, site and lift sat on the registers unmentioned. Each committed
              step is named with a link and its input is locked (above), so a retry is legible
              rather than mysterious, and nothing is ever created twice. */}
          {/* Session ASQ, Item 3's review, F5: the new lift's customer belongs to a person whose data was erased. Raise it
              again would only be refused again; this one goes ahead, and the server audits it. */}
          {creatingNewLift && chain.chainErased && (
            <ErasedPersonNotice
              message={chain.chainErased}
              busy={busy}
              onGoAhead={() => {
                chain.allowErasedPerson();
                void submit();
              }}
            />
          )}

          {creatingNewLift && (chain.chainFailure || (error && chain.created.customer)) && (
            <div role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm">
              <p className="font-heading tracking-wide text-ele-danger">
                {chain.chainFailure ? "Could not finish creating the records" : "The records were created, but the work was not raised"}
              </p>
              <p className="mt-1 text-ele-strong">{chain.chainFailure ? chain.chainFailure.message : error}</p>
              {(chain.created.customer || chain.created.site) && (
                <>
                  <p className="mt-2 text-ele-body">Nothing was lost. This is what has been created so far:</p>
                  <ul className="mt-1 space-y-1">
                    {chain.created.customer && (
                      <li>
                        Customer:{" "}
                        <Link to={`/office/customers/${chain.created.customer.id}`} className="text-ele-green hover:underline">
                          {chain.created.customer.name}
                        </Link>
                      </li>
                    )}
                    {chain.created.site && (
                      <li>
                        Site:{" "}
                        <Link to={`/office/sites/${chain.created.site.id}`} className="text-ele-green hover:underline">
                          {chain.created.site.name}
                        </Link>
                      </li>
                    )}
                    {chain.created.asset && <li className="text-ele-body">Lift: created at the site above.</li>}
                  </ul>
                </>
              )}
              <p className="mt-2 text-ele-body">
                {chain.chainFailure
                  ? `Press the button again to carry on from the ${CHAIN_STEP_LABEL[chain.chainFailure.step]}. Anything listed above is reused, never created twice.`
                  : "Press the button again to raise the work. The records above are reused, never created twice."}
              </p>
            </div>
          )}

          {/* The plain error line, for every case the panel above does not already explain. */}
          {error && !(creatingNewLift && chain.created.customer) && (
            <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {error}
            </p>
          )}

          {/* Session SC9B: a flag, never a block. One amber line when the customer's account is on stop; the same press raises the work. */}
          <AccountOnStopLine customerId={repairNoLift ? repairCustomerId || null : assetCustomerId ?? preset?.customerId ?? null} />
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={() => !busy && onClose()}
              disabled={busy}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            {/* Per the branded-confirm rule. The label stays "Raise it" for all four types: the
                type is already chosen above, and restating it here would read as a second choice.
                The CONFIRM label does name what is being created when a new lift's details were
                typed (Session AOI), matching CalloutsPage.tsx's own dynamic confirm for the
                identical situation: this one press creates a customer, a site and a lift as well as
                raising the work. */}
            <ConfirmButton
              onConfirm={() => void submit()}
              label="Raise it"
              confirmLabel={
                creatingNewLift
                  ? `Press again to create ${(chain.created.customer?.name || newLiftForm.customerName).trim() || "this customer"} at ${
                      (chain.created.site?.name || newLiftForm.siteName).trim() || "this site"
                    }`
                  : "Press again to confirm"
              }
              busy={busy}
              busyLabel="Raising..."
              disabled={!canSubmit}
              className={buttonClasses("secondary", "md", MODAL_ACTION_BTN)}
            />
          </div>
          </>)}
        </form>
      )}
    </ModalShell>
  );
}

// The server's own sentence where it sent one (a retired lift, a refused clash), this page's
// fallback where it did not. Never a raw error object: the office reads this.
function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: unknown } | undefined;
    if (typeof body?.message === "string" && body.message.trim()) return body.message.trim();
  }
  return fallback;
}
